<script setup lang="ts">
  import { computed } from 'vue';

  import type { Contact, Partner } from '@stuut/api/models';
  import { Accordion, AccordionContent, AccordionItem } from '@ui/components/accordion';
  import { Badge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { useCopyToClipboard } from '@/composables/useCopyToClipboard';
  import { useProfile } from '@/store';

  import ContactDetails from './ContactDetails.vue';
  import ContactDropdown from './ContactDropdown.vue';
  import type { ContactDropdownEmits } from './ContactDropdown.vue';

  interface Props {
    contact: Contact;
    disableDropdown?: boolean;
    expandable?: boolean;
    expanded?: boolean;
    partner?: Partner;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<
    ContactDropdownEmits & {
      (e: 'toggle'): void;
      (e: 'makePrimary', contact: Contact, channel: string): void;
      (e: 'enableContact', contact: Contact, channel: string, event_source: string): void;
      (e: 'disableContact', contact: Contact, channel: string, event_source: string): void;
      (e: 'edit', contact: Contact): void;
      (e: 'delete', contact: Contact): void;
    }
  >();
  const profileStore = useProfile();
  const isSmsOutreachEnabled = computed(
    () => profileStore.profile?.organization?.is_sms_outreach_enabled ?? false,
  );

  const { copy } = useCopyToClipboard();
</script>

<template>
  <Accordion :model-value="expanded ? '1' : '0'" type="single" collapsible class="w-full">
    <AccordionItem value="1" class="border-b-0">
      <div class="flex flex-row justify-between gap-x-4 items-start">
        <div
          class="flex flex-col gap-y-1 w-0 flex-1 overflow-hidden *:text-ellipsis *:overflow-x-hidden *:text-nowrap"
        >
          <div class="text-xs font-medium truncate">
            {{ contact?.name }}
          </div>

          <Tooltip v-if="contact?.email">
            <TooltipTrigger as-child>
              <span
                class="text-muted-foreground cursor-pointer"
                :class="{ 'line-through': contact?.is_email_disabled }"
                @click="copy(contact.email)"
              >
                {{ contact?.email }}
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{{ contact?.email }}</TooltipContent>
          </Tooltip>

          <span v-else class="text-muted-foreground truncate">{{ contact?.email }}</span>
        </div>

        <div class="flex flex-row gap-x-2 items-center">
          <Tooltip v-if="contact?.isPartnerAccountContact">
            <TooltipTrigger as-child>
              <i class="fa far fa-building block mr-1 mt-0.5" />
            </TooltipTrigger>
            <TooltipContent class="text-2xs">
              This contact information is derived from
              {{
                props.partner?.linked_integration.integration.display_name
                  ? props.partner.linked_integration.integration.display_name
                  : 'an external source'
              }}.
            </TooltipContent>
          </Tooltip>

          <Badge
            v-if="!contact?.is_primary_email"
            :variant="contact?.is_email_disabled ? 'destructive' : 'open'"
            size="xs"
          >
            <i class="fa far fa-envelope" />
          </Badge>

          <Badge
            v-if="!contact?.is_primary_phone"
            :variant="contact?.is_phone_disabled ? 'destructive' : 'open'"
            size="xs"
          >
            <i class="fa far fa-phone" />
          </Badge>
          <Badge
            v-if="contact?.is_primary_email || contact?.is_primary_phone"
            variant="open"
            size="xs"
          >
            <i v-if="contact?.is_primary_email" class="fa far fa-envelope" />
            <i v-if="contact?.is_primary_phone" class="fa far fa-phone" />
            <span>Primary</span>
          </Badge>
          <Badge
            v-if="isSmsOutreachEnabled"
            :variant="contact?.is_sms_disabled ? 'destructive' : 'open'"
            size="xs"
          >
            <i class="fa far fa-sms" />
          </Badge>

          <ContactDropdown
            :contact="contact"
            :show-enable-disable="true"
            @edit="emits('edit', contact)"
            @delete="emits('delete', contact)"
            @make-primary="(contact, channel) => emits('makePrimary', contact, channel)"
            @enable-contact="(contact, channel) => emits('enableContact', contact, channel)"
            @disable-contact="(contact, channel) => emits('disableContact', contact, channel)"
          >
            <template #trigger>
              <Button
                size="icon"
                variant="outline"
                class="size-6 text-xs!"
                :disabled="props.disableDropdown"
              >
                <i class="fa far fa-ellipsis-h text-xs" />
              </Button>
            </template>
          </ContactDropdown>

          <Button
            size="icon"
            variant="ghost"
            class="size-6 transition-all [&[data-state=open]>i]:rotate-180"
            :data-state="expanded ? 'open' : ''"
            @click="emits('toggle')"
          >
            <i class="fa far fa-chevron-down transition-transform duration-200 text-xs!" />
          </Button>
        </div>
      </div>

      <AccordionContent class="p-0 pt-4">
        <ContactDetails :contact="contact" class="text-xs" />
      </AccordionContent>
    </AccordionItem>
  </Accordion>
</template>

<style scoped></style>
